import { ReactElement } from 'react';
import { LegendAttributes } from '../../constants/telosair-interfaces';
import { getIconByPointStyle } from '../../_helpers/graph';

type Props<T> = {
  legend: LegendAttributes;
  handleLegendClick: (val: T) => void;
  handleGroupLegendClick?: (val: string, active: boolean, groupKey: string) => void;
  activeDatasets: T[];
};

/**
 * Legend component.
 * @param props - Properties.
 * @returns Legend.
 */
export default function Legend<T>(props: Props<T>): ReactElement {
  const { legend, activeDatasets, handleLegendClick, handleGroupLegendClick } = props;
  /**
   * Check wheather Dataset is selected or not.
   * @param  parameter - Parameter.
   * @returns  true or false.
   */
  const isDatasetSelected = (parameter: string): boolean => {
    return activeDatasets.filter((item) => item === parameter).length > 0;
  };

  /**
   * Check wheather Dataset is selected or not.
   * @param  value - Value.
   * @returns  true or false.
   */
  const isGroupSelected = (value: string): boolean => {
    return activeDatasets.filter((item: any) => item?.includes(value)).length > 0;
  };

  /**
   * Get the CSS class for a parameter based on its active status.
   * @param  value - Value.
   * @param isGroup - Is it a group.
   * @returns  The CSS class.
   */
  const parameterClass = (value: string, isGroup?: boolean): string => {
    if (isGroup) {
      return isGroupSelected(value) ? 'text-white' : 'text-gray-400';
    }
    return isDatasetSelected(value) ? 'text-white' : 'text-gray-400';
  };

  if (legend.isGroupped) {
    return (
      <div className="flex justify-between items-center w-full gap-5">
        {Object.entries(legend.grouppedLegends).map(([key, item]) => {
          return (
            <div className="flex gap-2 flex-wrap" key={key}>
              {item.map((eachLegend) => (
                <button
                  key={eachLegend.value}
                  type="button"
                  onClick={() =>
                    handleGroupLegendClick &&
                    handleGroupLegendClick(eachLegend.value, isGroupSelected(eachLegend.value), key)
                  }
                  className={`gap-1 items-center ${parameterClass(eachLegend.value, true)} ${
                    eachLegend.active ? 'flex' : 'hidden'
                  } cursor-pointer `}
                >
                  <div
                    className="text-sm"
                    style={{
                      color: eachLegend.color,
                      opacity: !isGroupSelected(eachLegend.value) ? 0.7 : 1
                    }}
                  >
                    {eachLegend.pointStyle ? (
                      getIconByPointStyle(eachLegend.pointStyle)
                    ) : (
                      <span className="material-icons text-sm">circle</span>
                    )}
                    {/* {eachLegend.pointStyle ? POINT_STYLE_ICON[] : 'circle'} */}
                  </div>
                  <div className="text-sm font-normal">{eachLegend.label}</div>
                </button>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <>
      {legend.legends.map((element) => (
        <button
          key={element.key}
          type="button"
          onClick={() => handleLegendClick(element.key as T)}
          className={`flex gap-1 items-center ${parameterClass(element.key)} cursor-pointer ${
            !element.display && 'hidden'
          }`}
        >
          <span
            className="material-icons text-sm"
            style={{
              color: element.color,
              opacity: !isDatasetSelected(element.key) ? 0.7 : 1
            }}
          >
            circle
          </span>
          <div className="text-sm font-normal">{element.label}</div>
        </button>
      ))}
    </>
  );
}
